import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { Element } from "@nkitaa/builder-engine";
import { useWBTheme, useEventTimezone } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { resolveText, type LangText } from "../lib/resolve-text";
import { t } from "../locale";
import { wallPartsToIso } from "../../lib/utils/datetime-tz";
import {
  type BorderSide,
  type FieldSpacing,
  type ParentFilter,
  CompanionIndexOverrideContext,
  WB_BOOKING_CAPACITY_EVENT,
  WB_BOOKING_SUMMARY_EVENT,
  WB_BOOKING_REGISTER_EVENT,
  WB_COMPANION_BADGE_COLORS_EVENT,
  WB_COMPANION_FIELD_LABELS_EVENT,
  WB_COMPANION_MODAL_EVENT,
  WB_VALIDATE_EVENT,
  dispatchCachedEvent,
  readCachedEvent,
  scanCachedEvents,
  classNames,
  evaluateVisibilityFilter,
  extractUtilityClasses,
  formatOverviewValue,
  getBorderClasses,
  nonEmptyString,
  sanitizeHtml,
  stopBuilderSelection,
  toCssLength,
  isPlainRecord,
  useDataInputDraftSnapshot,
  useDataInputReadOnly,
  useDataInputVerificationSnapshot,
  withoutAdditionalFields,
  asOverviewStringValue,
  resolveRecordValueByField,
} from "./shared";
import { IconCalendarPlus, IconCheck, IconClipboardList, IconPencil, IconX } from "@tabler/icons-react";
import { Button } from "./button";
import { FormModal } from "./formModal";
import { shouldValidateField } from "./formInput";

/** Convert Tailwind border-radius class to px number for runtime inline styles */
function parseBorderRadiusPx(tw: string): number {
  const map: Record<string, number> = {
    "rounded-none": 0, "rounded-sm": 2, "rounded": 4, "rounded-md": 6,
    "rounded-lg": 8, "rounded-xl": 12, "rounded-2xl": 16, "rounded-3xl": 24,
  };
  return map[tw] ?? 8;
}

/** Convert Tailwind font size class to CSS value for runtime inline styles */
function tailwindFontSizeToCss(tw: string): string {
  const map: Record<string, string> = {
    "text-xs": "0.75rem", "text-sm": "0.875rem", "text-base": "1rem",
    "text-lg": "1.125rem", "text-xl": "1.25rem",
  };
  return map[tw] ?? "1rem";
}

/**
 * Map fieldSpacing → asymmetric Tailwind gap classes. Mirrors the editor
 * (FieldsGroup.tsx / CompanionsGroup.tsx fieldSpacingToClass) exactly so the
 * runtime grid matches the WYSIWYG editor.
 */
function fieldSpacingToClass(spacing: FieldSpacing | undefined): string {
  return spacing === "extraSmall"
    ? "gap-y-1 wb-sm:gap-x-2 wb-sm:gap-y-1"
    : spacing === "small"
      ? "gap-y-1 wb-sm:gap-x-3 wb-sm:gap-y-1"
      : spacing === "large"
        ? "gap-y-3 wb-sm:gap-x-5 wb-sm:gap-y-3"
        : spacing === "extraLarge"
          ? "gap-y-4 wb-sm:gap-x-6 wb-sm:gap-y-4"
          : "gap-y-2 wb-sm:gap-x-4 wb-sm:gap-y-2";
}

export function FieldsGroup({
  children,
  isVisible: _isVisible = true,
  groupLabel,
  groupLabelColor,
  backgroundColor = "transparent",
  padding = "p-0",
  border = false,
  borderColor,
  borderWidth = "border-2",
  borderStyle = "border-solid",
  borderSides,
  borderRadius,
  fieldSpacing = "medium",
}: PropsWithChildren<{
  isVisible?: boolean;
  groupLabel?: string;
  groupLabelColor?: string;
  backgroundColor?: string;
  padding?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
  borderRadius?: string;
  fieldSpacing?: FieldSpacing;
}>) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  // Ignore serialized isVisible — the parent wrapper div in DataInput controls
  // section visibility via display:none. Always render children so form inputs
  // stay in the DOM for value collection and are visible when the section is active.
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );
  const fieldSpacingClass = fieldSpacingToClass(fieldSpacing);
  return (
    <div
      className={classNames("w-full", padding, borderClasses, roundedClasses)}
      style={{
        backgroundColor:
          backgroundColor !== "transparent" ? backgroundColor : undefined,
        border: border ? undefined : "none",
        borderColor: border
          ? (borderColor ?? currentColorPalette.secondary)
          : undefined,
        borderRadius: roundedClasses
          ? undefined
          : toCssLength(borderRadius ?? currentRounding),
      }}
    >
      {groupLabel ? (
        <p
          style={{
            gridColumn: "1 / -1",
            margin: 0,
            marginBottom: "0.75rem",
            color: groupLabelColor ?? currentColorPalette.primary,
          }}
        >
          {groupLabel}
        </p>
      ) : null}
      <div
        data-wb-field-grid=""
        className={classNames(
          "w-full flex flex-col wb-sm:grid wb-sm:grid-cols-12",
          fieldSpacingClass,
        )}
      >
        {children}
      </div>
    </div>
  );
}

function resolveSessionCompanionCount(session: unknown): number {
  if (!isPlainRecord(session)) return 0;
  const user = isPlainRecord(session.user) ? session.user : undefined;
  const companions = user?.companions ?? session.companions;
  return Array.isArray(companions) ? companions.length : 0;
}


const ACCEPTANCE_STATE_COLORS: Record<string, { bg: string; text: string }> = {
  ACCEPTED: { bg: "#dcfce7", text: "#166534" },
  DECLINED: { bg: "#fee2e2", text: "#991b1b" },
  PENDING: { bg: "#fef9c3", text: "#854d0e" },
  SYSTEM: { bg: "#f3f4f6", text: "#374151" },
};

type BadgeColorOverrides = {
  pendingBg?: string | undefined;
  pendingText?: string | undefined;
  acceptedBg?: string | undefined;
  acceptedText?: string | undefined;
  declinedBg?: string | undefined;
  declinedText?: string | undefined;
  systemBg?: string | undefined;
  systemText?: string | undefined;
};

export function CompanionStateBadge({ state, secondaryColor, badgeColors }: { state: string; secondaryColor: string; badgeColors?: BadgeColorOverrides }) {
  const defaults = ACCEPTANCE_STATE_COLORS[state] ?? {
    bg: secondaryColor + "1a",
    text: secondaryColor,
  };
  const overrideMap: Record<string, { bg?: string | undefined; text?: string | undefined }> = badgeColors ? {
    ACCEPTED: { bg: badgeColors.acceptedBg, text: badgeColors.acceptedText },
    DECLINED: { bg: badgeColors.declinedBg, text: badgeColors.declinedText },
    PENDING: { bg: badgeColors.pendingBg, text: badgeColors.pendingText },
    SYSTEM: { bg: badgeColors.systemBg, text: badgeColors.systemText },
  } : {};
  const override = overrideMap[state];
  const colors = {
    bg: override?.bg || defaults.bg,
    text: override?.text || defaults.text,
  };
  return (
    <span
      style={{
        fontSize: "0.625rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.025em",
        padding: "0.125rem 0.375rem",
        borderRadius: "9999px",
        backgroundColor: colors.bg,
        color: colors.text,
        lineHeight: 1.4,
      }}
    >
      {state}
    </span>
  );
}

/**
 * Scan a container for `[data-field-variable]` elements and return
 * a map of fieldVariable → value. Used to capture companion field
 * values from the modal before it closes.
 */
function scanFieldValues(
  container: HTMLElement,
  eventTz: string,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const els = container.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-field-variable]");
  els.forEach((el) => {
    const variable = el.dataset.fieldVariable;
    if (!variable) return;
    if (el instanceof HTMLInputElement && el.type === "checkbox") {
      result[variable] = el.checked;
    } else if (el instanceof HTMLSelectElement && el.multiple) {
      result[variable] = Array.from(el.selectedOptions).map((o) => o.value);
    } else {
      let value: unknown = (el as HTMLInputElement).value;
      // Companion bridge invariant: store DATE_TIME as UTC ISO so the hidden
      // input (rendered without `data-tz-mode`) ships the right value at
      // submit time. collectFieldValues then passes it through unchanged.
      if (
        el.dataset.tzMode === "datetime-local" &&
        typeof value === "string" &&
        value !== ""
      ) {
        // Read only first two `:`-separated time components so Safari's
        // optional `:ss` is dropped without touching the original value.
        const [datePart, timePart] = value.split("T");
        if (datePart && timePart) {
          const [yStr, mStr, dStr] = datePart.split("-");
          const [hStr, minStr] = timePart.split(":");
          if (yStr && mStr && dStr && hStr && minStr) {
            const year = parseInt(yStr, 10);
            const month = parseInt(mStr, 10) - 1;
            const day = parseInt(dStr, 10);
            const hour = parseInt(hStr, 10);
            const minute = parseInt(minStr, 10);
            if (
              Number.isFinite(year) && Number.isFinite(month) &&
              Number.isFinite(day) && Number.isFinite(hour) &&
              Number.isFinite(minute)
            ) {
              value = wallPartsToIso(
                { year, month, day, hour, minute },
                eventTz,
              );
            }
          }
        }
      }
      result[variable] = value;
    }
  });
  return result;
}

/**
 * Derive a display name from saved companion data (firstName + lastName).
 */
function companionDisplayName(data: Record<string, unknown> | undefined): string {
  if (!data) return t("companionNoName");
  const str = (key: string) => (typeof data[key] === "string" ? data[key].trim() : "");
  const first = str("firstName");
  const last = str("lastName");
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;
  if (str("email")) return str("email");
  if (str("company")) return str("company");
  if (str("phone")) return str("phone");
  return t("companionNoName");
}

/**
 * Derive a secondary display line (shown below the primary name).
 * If the primary used firstName/lastName, show email/company/phone as secondary.
 */
function companionSecondaryLine(data: Record<string, unknown> | undefined): string | null {
  if (!data) return null;
  const str = (key: string) => (typeof data[key] === "string" ? data[key].trim() : "");
  const hasName = str("firstName") || str("lastName");
  if (hasName) {
    if (str("email")) return str("email");
    if (str("company")) return str("company");
    if (str("phone")) return str("phone");
  }
  return null;
}

/**
 * Scan a container for `[data-field-variable][data-field-label]` elements
 * and return a map of fieldVariable → label.
 */
function scanFieldLabels(container: HTMLElement): Record<string, string> {
  const result: Record<string, string> = {};
  const els = container.querySelectorAll<HTMLElement>("[data-field-variable][data-field-label]");
  els.forEach((el) => {
    const variable = el.dataset.fieldVariable;
    const label = el.dataset.fieldLabel;
    if (variable && label) result[variable] = label;
  });
  return result;
}

export function CompanionsGroup({
  children,
  isVisible: _isVisible = true,
  groupLabel,
  groupLabelColor,
  backgroundColor = "transparent",
  padding = "p-0",
  border = false,
  borderColor,
  borderWidth = "border-2",
  borderStyle = "border-solid",
  borderSides,
  borderRadius,
  fieldSpacing = "medium",
  maxCompanions = 1,
  maxCompanionsRules,
  modalBackgroundColor,
  modalBorderRadius,
  modalMaxWidth,
  modalTitleColor,
  modalTitleFontSize,
  modalFooterBorderColor,
  modalBackdropOpacity,
  cancelButtonLabel,
  cancelButtonBorderColor,
  cancelButtonTextColor,
  cancelButtonBackgroundColor,
  cancelButtonBorderRadius,
  confirmButtonLabel,
  confirmButtonBackgroundColor,
  confirmButtonTextColor,
  confirmButtonBorderRadius,
  summaryCardBorderColor,
  summaryCardBorderRadius,
  stateBadgePendingBg,
  stateBadgePendingText,
  stateBadgeAcceptedBg,
  stateBadgeAcceptedText,
  stateBadgeDeclinedBg,
  stateBadgeDeclinedText,
  stateBadgeSystemBg,
  stateBadgeSystemText,
  addButtonLabel,
  addButtonBorderColor,
  addButtonTextColor,
  editButtonLabel,
  editButtonColor,
  closeIconColor,
  modalBorderColor,
  cancelButtonBgHoverColor,
  cancelButtonTextHoverColor,
  cancelButtonBorderHoverColor,
  confirmButtonBgHoverColor,
  confirmButtonTextHoverColor,
}: PropsWithChildren<{
  isVisible?: boolean;
  groupLabel?: string;
  groupLabelColor?: string;
  backgroundColor?: string;
  padding?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
  borderRadius?: string;
  fieldSpacing?: FieldSpacing;
  maxCompanions?: number;
  maxCompanionsRules?: { filter: ParentFilter; maxValue: number }[];
  modalBackgroundColor?: string;
  modalBorderRadius?: string;
  modalMaxWidth?: string;
  modalTitleColor?: string;
  modalTitleFontSize?: string;
  modalFooterBorderColor?: string;
  modalBackdropOpacity?: number;
  cancelButtonLabel?: string;
  cancelButtonBorderColor?: string;
  cancelButtonTextColor?: string;
  cancelButtonBackgroundColor?: string;
  cancelButtonBorderRadius?: string;
  confirmButtonLabel?: string;
  confirmButtonBackgroundColor?: string;
  confirmButtonTextColor?: string;
  confirmButtonBorderRadius?: string;
  summaryCardBorderColor?: string;
  summaryCardBorderRadius?: string;
  stateBadgePendingBg?: string;
  stateBadgePendingText?: string;
  stateBadgeAcceptedBg?: string;
  stateBadgeAcceptedText?: string;
  stateBadgeDeclinedBg?: string;
  stateBadgeDeclinedText?: string;
  stateBadgeSystemBg?: string;
  stateBadgeSystemText?: string;
  addButtonLabel?: string;
  addButtonBorderColor?: string;
  addButtonTextColor?: string;
  editButtonLabel?: string;
  editButtonColor?: string;
  closeIconColor?: string;
  modalBorderColor?: string;
  cancelButtonBgHoverColor?: string;
  cancelButtonTextHoverColor?: string;
  cancelButtonBorderHoverColor?: string;
  confirmButtonBgHoverColor?: string;
  confirmButtonTextHoverColor?: string;
}>) {
  const { currentColorPalette, currentRounding, session } = useWBTheme();
  const dataInputReadOnly = useDataInputReadOnly();
  const sessionCompanionCount = resolveSessionCompanionCount(session);

  // Track companion slots as an array of session-companion indices.
  // Start empty when no session companions exist — cards only appear after save.
  const nextIdRef = useRef(Math.max(1, sessionCompanionCount));
  const [slots, setSlots] = useState<number[]>(() =>
    Array.from(
      { length: sessionCompanionCount },
      (_, i) => i,
    ),
  );

  // Saved companion field values keyed by companion index.
  // Hidden inputs are rendered from this state so collectFieldValues() works.
  const [companionData, setCompanionData] = useState<
    Record<number, Record<string, unknown>>
  >({});

  // Which companion slot is being edited (null = modal closed)
  const [editingSlot, setEditingSlot] = useState<number | null>(null);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [confirmHovered, setConfirmHovered] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Sync when session gains more companions than we have slots
  useEffect(() => {
    if (sessionCompanionCount > slots.length) {
      setSlots((prev) => {
        const next = [...prev];
        for (let i = prev.length; i < sessionCompanionCount; i++) {
          next.push(i);
        }
        nextIdRef.current = Math.max(nextIdRef.current, sessionCompanionCount);
        return next;
      });
    }
  }, [sessionCompanionCount]);

  // P9-F2: tell App when the companion modal opens/closes — visibility rules
  // inside the modal evaluate against the COMPANION's live values (overlaid),
  // and the overlay dies with the modal.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(WB_COMPANION_MODAL_EVENT, {
        detail: { open: editingSlot !== null },
      }),
    );
  }, [editingSlot]);

  const eventTz = useEventTimezone();
  const saveCompanionFromModal = useCallback(() => {
    if (editingSlot === null || !modalRef.current) return;
    // Validate before storing: the modal's own Save used to accept anything,
    // so a companion could be saved with an empty required name/email and the
    // main submit never saw the fields at all. `includeHidden` because the
    // modal card is position:fixed — every field inside it reports
    // `offsetParent === null` and would opt itself out of a normal pass.
    const detail = { errorCount: 0, includeHidden: true, errorNodes: [] as Element[] };
    modalRef.current.dispatchEvent(
      new CustomEvent(WB_VALIDATE_EVENT, { detail }),
    );
    if (detail.errorCount > 0) return;
    const values = scanFieldValues(modalRef.current, eventTz);
    const labels = scanFieldLabels(modalRef.current);
    setCompanionData((prev) => ({ ...prev, [editingSlot]: values }));
    if (Object.keys(labels).length > 0) {
      dispatchCachedEvent(WB_COMPANION_FIELD_LABELS_EVENT, labels);
    }
    setEditingSlot(null);
  }, [editingSlot, eventTz]);

  // Ignore serialized isVisible — parent wrapper div controls visibility.
  const resolvedGroupLabel = groupLabel;
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );
  const fieldSpacingClass = fieldSpacingToClass(fieldSpacing);

  // Phase 6: evaluate maxCompanionsRules (first match wins, fall back to scalar)
  const sessionUser = useMemo(() => {
    if (!isPlainRecord(session)) return {};
    const u = isPlainRecord(session.user) ? session.user : session;
    const additional = isPlainRecord(u.additionalFields) ? u.additionalFields : {};
    return { ...u, ...additional } as Record<string, unknown>;
  }, [session]);

  const resolvedMax = useMemo(() => {
    if (maxCompanionsRules && maxCompanionsRules.length > 0) {
      for (const rule of maxCompanionsRules) {
        if (evaluateVisibilityFilter(rule.filter, sessionUser)) {
          return rule.maxValue;
        }
      }
    }
    return maxCompanions ?? 1;
  }, [maxCompanionsRules, maxCompanions, sessionUser]);

  // Phase 7c: capacity-aware companion limits — aggregate across ALL BookingGroups
  const [capacityMap, setCapacityMap] = useState<Map<string, { treatmentCapacity: number; spotsLeft: number }>>(new Map());

  useEffect(() => {
    // Track registered offering IDs to listen for their namespaced capacity events
    const offeringIds = new Set<string>();
    const capacityListeners = new Map<string, (e: Event) => void>();

    const addCapacityListener = (oid: string) => {
      if (capacityListeners.has(oid)) return;
      const eventKey = `${WB_BOOKING_CAPACITY_EVENT}:${oid}`;
      const handler = (e: Event) => {
        const detail = (e as CustomEvent).detail as { treatmentCapacity: number; spotsLeft: number } | null;
        setCapacityMap((prev) => {
          const next = new Map(prev);
          if (detail) {
            next.set(oid, detail);
          } else {
            next.delete(oid);
          }
          return next;
        });
      };
      capacityListeners.set(oid, handler);
      window.addEventListener(eventKey, handler);
      // Check cached value
      const cached = readCachedEvent<{ treatmentCapacity: number; spotsLeft: number }>(eventKey);
      if (cached) {
        setCapacityMap((prev) => new Map(prev).set(oid, cached));
      }
    };

    const removeCapacityListener = (oid: string) => {
      const handler = capacityListeners.get(oid);
      if (handler) {
        window.removeEventListener(`${WB_BOOKING_CAPACITY_EVENT}:${oid}`, handler);
        capacityListeners.delete(oid);
      }
      setCapacityMap((prev) => {
        const next = new Map(prev);
        next.delete(oid);
        return next;
      });
    };

    const registerHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { offeringId: string; unregister?: boolean } | undefined;
      if (!detail?.offeringId) return;
      if (detail.unregister) {
        offeringIds.delete(detail.offeringId);
        removeCapacityListener(detail.offeringId);
      } else {
        offeringIds.add(detail.offeringId);
        addCapacityListener(detail.offeringId);
      }
    };

    window.addEventListener(WB_BOOKING_REGISTER_EVENT, registerHandler);

    // Discover BookingGroups that registered before this component mounted
    for (const { value } of scanCachedEvents<{ offeringId: string }>(WB_BOOKING_REGISTER_EVENT)) {
      if (value?.offeringId && !offeringIds.has(value.offeringId)) {
        offeringIds.add(value.offeringId);
        addCapacityListener(value.offeringId);
      }
    }

    return () => {
      window.removeEventListener(WB_BOOKING_REGISTER_EVENT, registerHandler);
      for (const [oid, handler] of capacityListeners) {
        window.removeEventListener(`${WB_BOOKING_CAPACITY_EVENT}:${oid}`, handler);
      }
    };
  }, []);

  const effectiveMax = useMemo(() => {
    let max = resolvedMax;
    if (capacityMap.size > 0) {
      // Most restrictive capacity across all active bookings
      for (const cap of capacityMap.values()) {
        const capacityCap = Math.max(0, cap.treatmentCapacity - 1);
        const spotsCap = Math.max(0, cap.spotsLeft - 1);
        max = Math.min(max, capacityCap, spotsCap);
      }
    }
    return Math.max(0, max);
  }, [resolvedMax, capacityMap]);

  // Auto-trim excess companion slots when effectiveMax decreases
  useEffect(() => {
    if (effectiveMax > 0 && slots.length > effectiveMax) {
      setSlots(prev => prev.slice(0, effectiveMax));
    }
  }, [effectiveMax]);

  const canAdd = slots.length < effectiveMax && !dataInputReadOnly;
  const canRemove = slots.length > 0 && !dataInputReadOnly;
  const secondaryColor = currentColorPalette.secondary;

  // Resolved modal/button/badge styling
  const resolvedSummaryBorderColor = summaryCardBorderColor || `${secondaryColor}40`;
  const resolvedEditColor = editButtonColor || secondaryColor;
  const resolvedEditLabel = editButtonLabel || t("companionEdit");
  const resolvedAddBorderColor = addButtonBorderColor || `${secondaryColor}60`;
  const resolvedAddTextColor = addButtonTextColor || secondaryColor;
  const resolvedAddLabel = addButtonLabel || t("addCompanion");
  const resolvedCancelBorderColor = cancelButtonBorderColor || `${secondaryColor}40`;
  const resolvedCancelTextColor = cancelButtonTextColor || secondaryColor;
  const resolvedConfirmBg = confirmButtonBackgroundColor || currentColorPalette.primary;
  const resolvedConfirmTextColor = confirmButtonTextColor || currentColorPalette.background;
  const resolvedSummaryRounding = summaryCardBorderRadius
    ? extractUtilityClasses(summaryCardBorderRadius, "rounded")
    : roundedClasses;
  const summaryBorderRadiusPx = resolvedSummaryRounding
    ? undefined
    : (toCssLength(summaryCardBorderRadius ?? borderRadius ?? currentRounding) ?? "10px");
  const resolvedCancelBgHover = cancelButtonBgHoverColor || (cancelButtonBackgroundColor === "transparent" ? (secondaryColor + "08") : cancelButtonBackgroundColor || "transparent");
  const resolvedCancelTextHover = cancelButtonTextHoverColor || resolvedCancelTextColor;
  const resolvedCancelBorderHover = cancelButtonBorderHoverColor || resolvedCancelBorderColor;
  const resolvedConfirmBgHover = confirmButtonBgHoverColor || (currentColorPalette.primary + "dd");
  const resolvedConfirmTextHover = confirmButtonTextHoverColor || resolvedConfirmTextColor;
  const modalStyleOverrides = {
    modalBackgroundColor,
    modalBorderRadius: modalBorderRadius ? parseBorderRadiusPx(modalBorderRadius) : undefined,
    modalMaxWidth: modalMaxWidth ? parseInt(modalMaxWidth) : undefined,
    modalTitleColor,
    modalTitleFontSize: modalTitleFontSize ? tailwindFontSizeToCss(modalTitleFontSize) : undefined,
    modalFooterBorderColor,
    modalBackdropOpacity,
    closeIconColor,
    modalBorderColor,
  };
  const badgeColors = {
    pendingBg: stateBadgePendingBg,
    pendingText: stateBadgePendingText,
    acceptedBg: stateBadgeAcceptedBg,
    acceptedText: stateBadgeAcceptedText,
    declinedBg: stateBadgeDeclinedBg,
    declinedText: stateBadgeDeclinedText,
    systemBg: stateBadgeSystemBg,
    systemText: stateBadgeSystemText,
  };

  // Broadcast badge colors so OverviewGroup can pick them up
  useEffect(() => {
    const hasBadgeOverrides = Object.values(badgeColors).some(Boolean);
    if (hasBadgeOverrides) {
      dispatchCachedEvent(WB_COMPANION_BADGE_COLORS_EVENT, badgeColors);
    }
  }, [stateBadgePendingBg, stateBadgePendingText, stateBadgeAcceptedBg, stateBadgeAcceptedText, stateBadgeDeclinedBg, stateBadgeDeclinedText, stateBadgeSystemBg, stateBadgeSystemText]);

  return (
    <div
      className={classNames("w-full", padding, borderClasses, roundedClasses)}
      style={{
        backgroundColor:
          backgroundColor !== "transparent" ? backgroundColor : undefined,
        border: border ? undefined : "none",
        borderColor: border
          ? (borderColor ?? secondaryColor)
          : undefined,
        borderRadius: roundedClasses
          ? undefined
          : toCssLength(borderRadius ?? currentRounding),
      }}
    >
      {resolvedGroupLabel ? (
        <p
          className="text-sm font-medium"
          style={{
            marginTop: 0,
            marginBottom: "0.75rem",
            color: groupLabelColor ?? currentColorPalette.primary,
          }}
        >
          {resolvedGroupLabel}
        </p>
      ) : null}

      {/* Compact summary cards — matches builder layout: [Badge] [Name] ... [IconPencil EditLabel] [IconX] */}
      <div>
        {slots.map((companionIdx, slotPos) => {
          const saved = companionData[companionIdx];
          const sessionCompanionState = resolveSessionCompanionState(session, companionIdx);
          // Don't render a card for unsaved slots (modal open, user hasn't confirmed yet)
          if (!saved && !sessionCompanionState) return null;
          const name = companionDisplayName(saved);
          const secondary = companionSecondaryLine(saved);
          return (
            <div
              key={companionIdx}
              className={classNames("w-full border p-3 wb-md:p-4", resolvedSummaryRounding || undefined, slotPos > 0 ? "mt-2" : undefined)}
              style={{
                borderRadius: summaryBorderRadiusPx,
                borderColor: resolvedSummaryBorderColor,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
                  <CompanionStateBadge
                    state={sessionCompanionState || "PENDING"}
                    secondaryColor={secondaryColor}
                    badgeColors={badgeColors}
                  />
                  <div style={{ minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: currentColorPalette.primary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {name}
                    </span>
                    {secondary ? (
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: secondaryColor,
                          opacity: 0.7,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        {secondary}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", flexShrink: 0, marginLeft: "0.5rem" }}>
                  {!dataInputReadOnly ? (
                    <button
                      type="button"
                      onClick={() => setEditingSlot(companionIdx)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: resolvedEditColor,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: "0.25rem 0.5rem",
                      }}
                    >
                      <IconPencil size={14} />
                      {resolvedEditLabel}
                    </button>
                  ) : null}
                  {canRemove ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSlots((prev) => prev.filter((_, i) => i !== slotPos));
                        setCompanionData((prev) => {
                          const next = { ...prev };
                          delete next[companionIdx];
                          return next;
                        });
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0.125rem",
                        color: secondaryColor,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <IconX size={14} />
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Hidden field bridge — always in DOM for collectFieldValues() */}
              {saved
                ? Object.entries(saved).map(([variable, value]) => (
                    <input
                      key={variable}
                      type="hidden"
                      data-field-variable={variable}
                      data-companion="true"
                      data-companion-index={String(companionIdx)}
                      value={typeof value === "boolean" ? (value ? "true" : "false") : String(value ?? "")}
                    />
                  ))
                : null}
            </div>
          );
        })}
      </div>

      {canAdd ? (
        <button
          type="button"
          onClick={() => {
            const nextId = nextIdRef.current;
            nextIdRef.current = nextId + 1;
            setSlots((prev) => [...prev, nextId]);
            // Open modal for the new companion
            setEditingSlot(nextId);
          }}
          className={roundedClasses || undefined}
          style={{
            width: "100%",
            marginTop: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            padding: "0.625rem",
            border: "1px dashed",
            borderColor: resolvedAddBorderColor,
            color: resolvedAddTextColor,
            background: "transparent",
            cursor: "pointer",
            borderRadius: roundedClasses
              ? undefined
              : (toCssLength(borderRadius ?? currentRounding) ?? "10px"),
            transition: "opacity 200ms",
            fontSize: "0.875rem",
            fontWeight: 500,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          <span>{resolvedAddLabel}</span>
        </button>
      ) : null}

      {/* Modal for editing a companion */}
      <FormModal
        open={editingSlot !== null}
        onClose={() => {
          // If the slot being edited has no saved data, remove it (user cancelled a new companion)
          if (editingSlot !== null && !companionData[editingSlot]) {
            setSlots((prev) => prev.filter((id) => id !== editingSlot));
          }
          setEditingSlot(null);
        }}
        title={
          editingSlot !== null
            ? t(
                "companionLabel",
                String(slots.indexOf(editingSlot) + 1),
              )
            : undefined
        }
        styleOverrides={modalStyleOverrides}
        footer={
          !dataInputReadOnly ? (
            <>
              <button
                type="button"
                onClick={() => {
                  // Remove unsaved slot on cancel (same logic as onClose)
                  if (editingSlot !== null && !companionData[editingSlot]) {
                    setSlots((prev) => prev.filter((id) => id !== editingSlot));
                  }
                  setEditingSlot(null);
                }}
                onMouseEnter={() => setCancelHovered(true)}
                onMouseLeave={() => setCancelHovered(false)}
                style={{
                  padding: "0.5rem 1rem",
                  border: `1px solid ${cancelHovered ? resolvedCancelBorderHover : resolvedCancelBorderColor}`,
                  borderRadius: cancelButtonBorderRadius ? parseInt(cancelButtonBorderRadius) : 8,
                  background: cancelHovered ? resolvedCancelBgHover : (cancelButtonBackgroundColor || "transparent"),
                  color: cancelHovered ? resolvedCancelTextHover : resolvedCancelTextColor,
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  transition: "background 0.15s, color 0.15s, border-color 0.15s",
                }}
              >
                {cancelButtonLabel || t("companionCancel")}
              </button>
              <button
                type="button"
                onClick={saveCompanionFromModal}
                onMouseEnter={() => setConfirmHovered(true)}
                onMouseLeave={() => setConfirmHovered(false)}
                style={{
                  padding: "0.5rem 1rem",
                  border: "none",
                  borderRadius: confirmButtonBorderRadius ? parseInt(confirmButtonBorderRadius) : 8,
                  background: confirmHovered ? resolvedConfirmBgHover : resolvedConfirmBg,
                  color: confirmHovered ? resolvedConfirmTextHover : resolvedConfirmTextColor,
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  transition: "background 0.15s, color 0.15s",
                }}
              >
                {confirmButtonLabel || t("companionSave")}
              </button>
            </>
          ) : undefined
        }
      >
        {editingSlot !== null ? (
          /* Validation host: the modal is PORTALED to document.body, so the
             fields inside it have no <form> ancestor and used to register no
             validator at all — "Save" then stored empty required fields in
             silence. `useFieldValidation` accepts this attribute as a host. */
          <div ref={modalRef} data-wb-validation-host="">
            <CompanionIndexOverrideContext.Provider value={editingSlot}>
              <div
                data-wb-field-grid=""
                className={classNames(
                  "w-full flex flex-col wb-sm:grid wb-sm:grid-cols-12",
                  fieldSpacingClass,
                )}
              >
                {children}
              </div>
            </CompanionIndexOverrideContext.Provider>
          </div>
        ) : null}
      </FormModal>
    </div>
  );
}

function resolveSessionCompanionState(session: unknown, companionIdx: number): string | undefined {
  if (!isPlainRecord(session)) return undefined;
  const user = isPlainRecord(session.user) ? session.user : undefined;
  const companions = (user?.companions ?? session.companions) as unknown;
  if (!Array.isArray(companions)) return undefined;
  const companion = companions[companionIdx];
  if (!isPlainRecord(companion)) return undefined;
  const state = companion.acceptanceState ?? companion.state;
  return typeof state === "string" && state ? state : undefined;
}

export type BookingSummary =
  | { mode: "AUTO_BOOKABLE"; treatmentName: string; dateTime: string; status?: string }
  | { mode: "REQUEST_BASED"; treatmentCount: number; windowCount: number; treatmentNames?: string[]; availabilityWindows?: { date: string; startTime: string; endTime: string }[] };

export function OverviewGroup({
  isVisible = false,
  backgroundColor,
  labelColor,
  valueColor,
  border = true,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
  borderRadius,
}: {
  isVisible?: boolean;
  backgroundColor?: string;
  labelColor?: string;
  valueColor?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
  borderRadius?: string;
}) {
  const { session, currentColorPalette, currentRounding } = useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const verificationSnapshot = useDataInputVerificationSnapshot();
  const draftSnapshot = useDataInputDraftSnapshot();

  // Listen for badge color overrides broadcast by CompanionsGroup
  const [overviewBadgeColors, setOverviewBadgeColors] = useState<BadgeColorOverrides | null>(
    () => readCachedEvent<BadgeColorOverrides>(WB_COMPANION_BADGE_COLORS_EVENT) ?? null,
  );
  useEffect(() => {
    const handler = (e: Event) => {
      setOverviewBadgeColors((e as CustomEvent).detail as BadgeColorOverrides);
    };
    window.addEventListener(WB_COMPANION_BADGE_COLORS_EVENT, handler);
    return () => window.removeEventListener(WB_COMPANION_BADGE_COLORS_EVENT, handler);
  }, []);

  // Listen for booking summaries from ALL BookingGroups (namespaced by offeringId)
  const [bookingSummaryMap, setBookingSummaryMap] = useState<Map<string, BookingSummary>>(new Map());

  useEffect(() => {
    const summaryListeners = new Map<string, (e: Event) => void>();

    const addSummaryListener = (oid: string) => {
      if (summaryListeners.has(oid)) return;
      const eventKey = `${WB_BOOKING_SUMMARY_EVENT}:${oid}`;
      const handler = (e: Event) => {
        const detail = (e as CustomEvent).detail as BookingSummary | null;
        setBookingSummaryMap((prev) => {
          const next = new Map(prev);
          if (detail) {
            next.set(oid, detail);
          } else {
            next.delete(oid);
          }
          return next;
        });
      };
      summaryListeners.set(oid, handler);
      window.addEventListener(eventKey, handler);
      // Check cached
      const cached = readCachedEvent<BookingSummary>(eventKey);
      if (cached) {
        setBookingSummaryMap((prev) => new Map(prev).set(oid, cached));
      }
    };

    const removeSummaryListener = (oid: string) => {
      const handler = summaryListeners.get(oid);
      if (handler) {
        window.removeEventListener(`${WB_BOOKING_SUMMARY_EVENT}:${oid}`, handler);
        summaryListeners.delete(oid);
      }
      setBookingSummaryMap((prev) => {
        const next = new Map(prev);
        next.delete(oid);
        return next;
      });
    };

    const registerHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { offeringId: string; unregister?: boolean } | undefined;
      if (!detail?.offeringId) return;
      if (detail.unregister) {
        removeSummaryListener(detail.offeringId);
      } else {
        addSummaryListener(detail.offeringId);
      }
    };

    window.addEventListener(WB_BOOKING_REGISTER_EVENT, registerHandler);

    // Discover BookingGroups that registered before OverviewGroup mounted
    for (const { value } of scanCachedEvents<{ offeringId: string }>(WB_BOOKING_REGISTER_EVENT)) {
      if (value?.offeringId) {
        addSummaryListener(value.offeringId);
      }
    }

    return () => {
      window.removeEventListener(WB_BOOKING_REGISTER_EVENT, registerHandler);
      for (const [oid, handler] of summaryListeners) {
        window.removeEventListener(`${WB_BOOKING_SUMMARY_EVENT}:${oid}`, handler);
      }
    };
  }, []);

  const bookingSummaries = useMemo(() => Array.from(bookingSummaryMap.values()), [bookingSummaryMap]);

  // Listen for companion field labels broadcast by CompanionsGroup
  const [companionFieldLabels, setCompanionFieldLabels] = useState<Record<string, string>>(
    () => readCachedEvent<Record<string, string>>(WB_COMPANION_FIELD_LABELS_EVENT) ?? {},
  );
  useEffect(() => {
    const handler = (e: Event) => {
      setCompanionFieldLabels((e as CustomEvent).detail as Record<string, string>);
    };
    window.addEventListener(WB_COMPANION_FIELD_LABELS_EVENT, handler);
    return () => window.removeEventListener(WB_COMPANION_FIELD_LABELS_EVENT, handler);
  }, []);

  if (!isVisible) return null;

  const activeSnapshot = verificationSnapshot ?? draftSnapshot;
  const values = activeSnapshot ? Object.entries(activeSnapshot.values) : [];
  const snapshotCompanions = activeSnapshot?.companions ?? [];
  const sessionCompanionsRaw = isPlainRecord(session)
    ? ((isPlainRecord(session.user)
        ? session.user.companions
        : session.companions) as unknown)
    : undefined;
  const sessionCompanions = Array.isArray(sessionCompanionsRaw)
    ? sessionCompanionsRaw.filter((item): item is Record<string, unknown> =>
        isPlainRecord(item),
      )
    : [];
  const companionCount = Math.max(
    snapshotCompanions.length,
    sessionCompanions.length,
  );
  const companions = Array.from({ length: companionCount }).map((_, idx) => {
    const sessionCompanion = sessionCompanions[idx];
    const snapshotCompanion = snapshotCompanions[idx];
    const sessionAdditional =
      sessionCompanion && isPlainRecord(sessionCompanion.additionalFields)
        ? sessionCompanion.additionalFields
        : undefined;
    const companionState = asOverviewStringValue(
      resolveRecordValueByField(snapshotCompanion ?? {}, "state") ??
        resolveRecordValueByField(sessionCompanion ?? {}, "state"),
    );
    const companionAcceptanceState = asOverviewStringValue(
      resolveRecordValueByField(snapshotCompanion ?? {}, "acceptanceState") ??
        resolveRecordValueByField(sessionCompanion ?? {}, "acceptanceState"),
    );

    return {
      acceptanceState: companionAcceptanceState,
      state: companionState,
      ...withoutAdditionalFields(sessionCompanion),
      ...(sessionAdditional ?? {}),
      ...withoutAdditionalFields(snapshotCompanion),
    };
  });
  const checkboxes = activeSnapshot?.checkboxes ?? [];
  const checkboxVarSet = new Set(
    checkboxes
      .map((cb) => cb.inputFieldVariable)
      .filter((v): v is string => Boolean(v)),
  );
  const filteredValues = values.filter(([key]) => !checkboxVarSet.has(key));

  const resolvedLabelColor = labelColor || currentColorPalette.secondary;
  const resolvedValueColor = valueColor || currentColorPalette.primary;
  const resolvedBorderColor = borderColor || currentColorPalette.secondary;
  const resolvedBorderRadius = borderRadius || currentRounding;
  const cardBg = backgroundColor || (currentColorPalette.secondary + "0a");
  const cardBorder = border !== false ? (resolvedBorderColor + "40") : "transparent";
  const roundedClasses = extractUtilityClasses(resolvedBorderRadius, "rounded");
  const overviewBorderClasses = getBorderClasses(border, borderWidth, borderStyle, borderSides);

  const cardClasses = classNames("flex flex-col gap-2 p-3", roundedClasses, overviewBorderClasses || undefined);
  const cardStyle = {
    backgroundColor: cardBg,
    border: overviewBorderClasses ? undefined : `1px solid ${cardBorder}`,
    borderColor: overviewBorderClasses ? cardBorder : undefined,
    borderRadius: roundedClasses ? undefined : resolvedBorderRadius,
  };

  if (!activeSnapshot) {
    return (
      <div style={{ fontSize: "0.875rem", opacity: 0.8 }}>
        {t("verificationPage")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {filteredValues.length > 0 ? (
        <div className={cardClasses} style={cardStyle}>
          {filteredValues.map(([key, value]) => (
            <div
              key={key}
              className="flex justify-between items-baseline gap-4"
            >
              <span
                className="text-sm font-medium shrink-0"
                style={{ color: resolvedLabelColor, opacity: 0.7 }}
              >
                {key}
              </span>
              <span
                className="text-sm text-right truncate"
                style={{ color: resolvedValueColor }}
              >
                {formatOverviewValue(value)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {/* Companion cards — each companion gets its own card with state badge */}
      {companions.length > 0 ? (
        companions.map((companion, idx) => {
          const companionState = typeof companion.acceptanceState === "string" && companion.acceptanceState
            ? companion.acceptanceState
            : typeof companion.state === "string" && companion.state
              ? companion.state
              : undefined;
          return (
            <div
              key={idx}
              className={cardClasses}
              style={cardStyle}
            >
              <div className="flex items-center gap-2 mb-1">
                <CompanionStateBadge
                  state={companionState || "PENDING"}
                  secondaryColor={resolvedLabelColor}
                  {...(overviewBadgeColors ? { badgeColors: overviewBadgeColors } : {})}
                />
                <span
                  className="text-xs font-medium"
                  style={{ color: resolvedLabelColor, opacity: 0.5 }}
                >
                  {t("companionLabel", String(idx + 1))}
                </span>
              </div>
              {Object.entries(companion)
                .filter(([key]) => key !== "inviteeId" && key !== "acceptanceState" && key !== "state")
                .map(([key, value]) => (
                <div
                  key={key}
                  className="flex justify-between items-baseline gap-4"
                >
                  <span
                    className="text-sm font-medium shrink-0"
                    style={{ color: resolvedLabelColor, opacity: 0.7 }}
                  >
                    {companionFieldLabels[key] || key}
                  </span>
                  <span
                    className="text-sm text-right truncate"
                    style={{ color: resolvedValueColor }}
                  >
                    {formatOverviewValue(value)}
                  </span>
                </div>
              ))}
            </div>
          );
        })
      ) : null}

      {/* Booking / appointment summary cards — one per BookingGroup */}
      {bookingSummaries.map((summary, idx) => (
        <div key={idx} className={cardClasses} style={cardStyle}>
          <span
            className="text-xs font-medium mb-1"
            style={{ color: resolvedLabelColor, opacity: 0.5 }}
          >
            {t("overviewBookingLabel")}
          </span>
          {summary.mode === "AUTO_BOOKABLE" ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <IconCalendarPlus size={16} style={{ color: resolvedLabelColor }} />
                <span
                  className="text-sm font-medium"
                  style={{ color: resolvedValueColor }}
                >
                  {summary.treatmentName}
                </span>
              </div>
              <span
                className="text-xs ml-6"
                style={{ color: resolvedLabelColor, opacity: 0.7 }}
              >
                {summary.dateTime}
              </span>
              {summary.status ? (() => {
                const sc: Record<string, { bg: string; color: string }> = {
                  CONFIRMED: { bg: "rgba(34,197,94,0.15)", color: "#16a34a" },
                  TENTATIVE: { bg: "rgba(234,179,8,0.15)", color: "#a16207" },
                  CANCELLED: { bg: "rgba(239,68,68,0.15)", color: "#dc2626" },
                  CHECKED_IN: { bg: "rgba(59,130,246,0.15)", color: "#2563eb" },
                };
                const st = sc[summary.status] ?? { bg: "rgba(100,116,139,0.15)", color: "#475569" };
                return (
                  <span className="ml-6 mt-1" style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 500, background: st.bg, color: st.color }}>
                    {summary.status.replace("_", " ")}
                  </span>
                );
              })() : null}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <IconClipboardList size={16} style={{ color: resolvedLabelColor }} />
              <span
                className="text-sm font-medium"
                style={{ color: resolvedValueColor }}
              >
                {t("slottingRequestSummary", String(summary.treatmentCount), String(summary.windowCount))}
              </span>
            </div>
          )}
        </div>
      ))}

      {checkboxes.length > 0 ? (
        <div className={cardClasses} style={cardStyle}>
          {checkboxes.map((item, idx) => (
            <div
              key={`${item.text}-${idx}`}
              className="flex justify-between items-center gap-4"
            >
              <span
                className="text-sm font-medium shrink-0"
                style={{ color: resolvedLabelColor, opacity: 0.7 }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    resolveText(item.text as LangText, currentLang, primaryLanguage),
                  ),
                }}
              />
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  fontSize: "0.875rem",
                  flexShrink: 0,
                  color: item.checked ? "#16a34a" : resolvedValueColor,
                }}
              >
                {item.checked ? (
                  <>
                    <IconCheck size={14} />
                    {t("yes")}
                  </>
                ) : (
                  <>
                    <IconX size={14} />
                    {t("no")}
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The stored boolean for a bound consent row (`inputFieldVariable`), from the
 *  session's user, its additionalFields, or the session's additionalFields —
 *  the lookup the prefill effect always made, lifted out on 2026-10-01 so the
 *  `useState` initializer can seed from it too. `"true"`/`"1"` count as ticked;
 *  anything else that is not a boolean is "no stored answer". */
function readSessionCheckboxBoolean(
  session: unknown,
  variable: string,
): boolean | undefined {
  if (!session || typeof session !== "object") return undefined;
  const s = session as Record<string, unknown>;
  const user = s.user;
  const userRec = user && typeof user === "object"
    ? (user as Record<string, unknown>)
    : undefined;
  const userAdditional = userRec?.additionalFields && typeof userRec.additionalFields === "object"
    ? (userRec.additionalFields as Record<string, unknown>)
    : undefined;
  const val =
    userRec?.[variable] ??
    userAdditional?.[variable] ??
    (s.additionalFields && typeof s.additionalFields === "object"
      ? (s.additionalFields as Record<string, unknown>)[variable]
      : undefined);
  if (typeof val === "boolean") return val;
  if (val === "true" || val === "1") return true;
  return undefined;
}

export function Checkboxes({
  isVisible: _isVisible = true,
  checkboxes,
  checkboxColor,
  checkmarkColor,
  textColor,
  requiredColor,
  checkboxShape,
}: {
  isVisible?: boolean;
  checkboxes?: {
    text: LangText;
    required: boolean;
    inputFieldVariable?: string;
  }[];
  checkboxColor?: string;
  checkmarkColor?: string;
  textColor?: string;
  requiredColor?: string;
  checkboxShape?: string;
}) {
  const { currentColorPalette, currentFonts, currentRounding, session } = useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const dataInputReadOnly = useDataInputReadOnly();
  const verificationSnapshot = useDataInputVerificationSnapshot();
  const draftSnapshot = useDataInputDraftSnapshot();
  // 2026-10-01: seeded from the session, not `{}`. The prefill effect below
  // runs after the first commit, and DataInput's mount capture reads the DOM
  // in that same commit — with `{}` it read an unticked box for a guest whose
  // stored `emailOptIn` is `true`, and an untouched submit wrote `false`.
  const [checkedState, setCheckedState] = useState<Record<number, boolean>>(
    () => {
      const seeded: Record<number, boolean> = {};
      (checkboxes ?? []).forEach((cb, idx) => {
        if (!cb.inputFieldVariable) return;
        const stored = readSessionCheckboxBoolean(session, cb.inputFieldVariable);
        if (stored !== undefined) seeded[idx] = stored;
      });
      return seeded;
    },
  );
  const [errorIndices, setErrorIndices] = useState<Set<number>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const checkedStateRef = useRef(checkedState);
  checkedStateRef.current = checkedState;
  // Ignore serialized isVisible — parent wrapper div controls visibility.
  const roundedClasses = extractUtilityClasses(
    checkboxShape ?? currentRounding,
    "rounded",
  );
  const resolvedCheckboxColor = checkboxColor ?? currentColorPalette.primary;
  const resolvedCheckmarkColor =
    checkmarkColor ?? currentColorPalette.background;
  const resolvedErrorColor = requiredColor ?? "#ef4444";
  const items = (checkboxes ?? []).map((it) => ({
    ...it,
    text: resolveText(it.text, currentLang, primaryLanguage),
  }));
  const checkboxTextsKey = items
    .map((item) => `${item.text}\0${item.inputFieldVariable ?? ""}`)
    .join("\0");

  // Pre-populate checkbox state from verification/draft snapshot or session.
  // Re-runs when checkboxes change or when snapshot changes (e.g. returning
  // from verification view via Update button).
  const snapshotRef = useRef(verificationSnapshot);
  useEffect(() => {
    // Only re-run on snapshot change if it was cleared (Update button click)
    const snapshotCleared =
      snapshotRef.current !== null && verificationSnapshot === null;
    snapshotRef.current = verificationSnapshot;

    // On initial mount or when checkbox items change, always populate.
    // On snapshot clear (Update click), re-populate from prior data.
    const source = verificationSnapshot ?? draftSnapshot;
    const initial: Record<number, boolean> = {};
    let populated = false;

    // First try snapshot checkboxes (matched by text)
    if (source?.checkboxes && source.checkboxes.length > 0) {
      items.forEach((item, idx) => {
        const match = source.checkboxes.find(
          (cb) => resolveText(cb.text as LangText, currentLang, primaryLanguage) === item.text,
        );
        if (match) {
          initial[idx] = match.checked;
          populated = true;
        }
      });
    }

    // Fallback: try snapshot values or session by inputFieldVariable
    if (!populated) {
      items.forEach((item, idx) => {
        const variable = item.inputFieldVariable;
        if (!variable) return;
        // Check snapshot values
        const snapVal = source?.values[variable];
        if (typeof snapVal === "boolean") {
          initial[idx] = snapVal;
          populated = true;
          return;
        }
        // Check session
        const stored = readSessionCheckboxBoolean(session, variable);
        if (stored !== undefined) {
          initial[idx] = stored;
          populated = true;
        }
      });
    }

    // 2026-10-01: a bound row with a stored session boolean — order is
    // review snapshot → TOUCHED draft → session → untouched draft. A pristine
    // draft (DataInput's mount/step DOM read before any interaction) may have
    // been taken before this widget's prefill reached the DOM, so it says
    // `false` for a stored `true`; letting it win above (text match or
    // `values[variable]`) silently un-ticked `emailOptIn` on an untouched
    // submit. Only when the source IS that pristine draft (or nothing) does the
    // session override here; a review snapshot or a draft the guest touched
    // keeps its answer.
    if (verificationSnapshot === null && (!draftSnapshot || draftSnapshot.pristine)) {
      items.forEach((item, idx) => {
        const variable = item.inputFieldVariable;
        if (!variable) return;
        const stored = readSessionCheckboxBoolean(session, variable);
        if (stored === undefined) return;
        initial[idx] = stored;
        populated = true;
      });
    }

    if (populated) {
      setCheckedState(initial);
    } else if (!snapshotCleared) {
      // Only reset if not a snapshot-clear (avoid wiping state on Update click
      // when no prior data is available)
      setCheckedState({});
    }
    setErrorIndices(new Set());
  }, [checkboxTextsKey, verificationSnapshot, session, draftSnapshot]);

  useEffect(() => {
    const form = containerRef.current?.closest("form");
    if (!form) return undefined;

    const handleReset = () => {
      setCheckedState({});
      setErrorIndices(new Set());
    };

    const handleValidate = (e: Event) => {
      if (dataInputReadOnly) return;
      // Skip if not visible — same contract as the form fields: a SUBMIT pass
      // opts into hidden ones (`includeHidden`), because a step the visitor
      // jumped past would otherwise be checked by nobody.
      const consentDetail = (e as CustomEvent).detail as
        | { errorCount: number; includeHidden?: boolean; hiddenErrorNodes?: Element[] }
        | undefined;
      if (
        !shouldValidateField(
          containerRef.current,
          consentDetail?.includeHidden === true,
        )
      )
        return;

      const newErrors = new Set<number>();
      items.forEach((item, idx) => {
        if (item.required && !checkedStateRef.current[idx]) {
          newErrors.add(idx);
        }
      });

      setErrorIndices(newErrors);
      if (newErrors.size > 0 && consentDetail) {
        consentDetail.errorCount += newErrors.size;
        // Report as hidden when it IS hidden, so the submit gate can decide
        // between jumping the visitor here and letting the server answer —
        // an unreported hidden error would dead-end the submit button.
        if (
          consentDetail.includeHidden &&
          consentDetail.hiddenErrorNodes &&
          containerRef.current &&
          containerRef.current.offsetParent === null
        )
          for (let i = 0; i < newErrors.size; i++)
            consentDetail.hiddenErrorNodes.push(containerRef.current);
      }
    };

    form.addEventListener("reset", handleReset);
    form.addEventListener(WB_VALIDATE_EVENT, handleValidate);
    return () => {
      form.removeEventListener("reset", handleReset);
      form.removeEventListener(WB_VALIDATE_EVENT, handleValidate);
    };
  }, [items, dataInputReadOnly]);

  return (
    <div
      ref={containerRef}
      className="flex flex-col space-y-3"
    >
      {items.map((item, idx) => {
        const checkboxFieldVariable = nonEmptyString(item.inputFieldVariable);
        const isDisabled = dataInputReadOnly;
        const isChecked = Boolean(checkedState[idx]);
        const hasError = errorIndices.has(idx);
        return (
          <div key={`${item.text}-${idx}`}>
            <label
              className="flex items-start gap-2.5"
              style={{
                color: hasError
                  ? resolvedErrorColor
                  : (textColor ?? currentColorPalette.secondary),
                fontFamily: currentFonts.body,
                cursor: isDisabled ? "not-allowed" : "pointer",
              }}
              onClick={stopBuilderSelection}
              onMouseDown={stopBuilderSelection}
              onPointerDown={stopBuilderSelection}
            >
              <span
                className={classNames(
                  "shrink-0 mt-0.5 flex items-center justify-center w-4.5 h-4.5 border transition-colors",
                  roundedClasses || undefined,
                )}
                style={{
                  borderColor: hasError ? resolvedErrorColor : resolvedCheckboxColor,
                  backgroundColor: isChecked
                    ? (hasError ? resolvedErrorColor : resolvedCheckboxColor)
                    : "transparent",
                  ...(hasError
                    ? { boxShadow: `0 0 0 1px ${resolvedErrorColor}30` }
                    : {}),
                }}
              >
                {isChecked ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={resolvedCheckmarkColor}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                ) : null}
              </span>
              <input
                type="checkbox"
                className="hidden"
                data-checkbox-text={item.text}
                data-checkbox-field-variable={checkboxFieldVariable}
                data-field-variable={checkboxFieldVariable}
                data-required={item.required ? "true" : "false"}
                checked={isChecked}
                disabled={isDisabled}
                onChange={(event) => {
                  if (isDisabled) return;
                  const newChecked = event.target.checked;
                  setCheckedState((prev) => ({
                    ...prev,
                    [idx]: newChecked,
                  }));
                  if (hasError && newChecked) {
                    setErrorIndices((prev) => {
                      const next = new Set(prev);
                      next.delete(idx);
                      return next;
                    });
                  }
                }}
                onClick={stopBuilderSelection}
                onMouseDown={stopBuilderSelection}
                onPointerDown={stopBuilderSelection}
              />
              <span
                className="text-sm leading-snug"
                dangerouslySetInnerHTML={{
                  __html:
                    sanitizeHtml(item.text) +
                    (item.required
                      ? `<span style="color: ${resolvedErrorColor};" class="ml-0.5">*</span>`
                      : ""),
                }}
              />
              {hasError ? (
                <span
                  className="text-xs block mt-0.5"
                  style={{ color: resolvedErrorColor }}
                >
                  {t("checkboxRequired")}
                </span>
              ) : null}
            </label>
          </div>
        );
      })}
    </div>
  );
}

export function CheckboxesGroup({
  children,
  isVisible = true,
  privacyQueryEnabled = true,
}: PropsWithChildren<{ isVisible?: boolean; privacyQueryEnabled?: boolean }>) {
  if (!isVisible) return null;
  if (!privacyQueryEnabled) return null;
  return <div>{children ?? <Element is={Checkboxes} id="checkboxes" />}</div>;
}

export function SubmitGroup({
  children,
  isVisible = true,
  buttonFormSpacing = "mt-3 wb-md:mt-4 wb-lg:mt-5",
}: PropsWithChildren<{ isVisible?: boolean; buttonFormSpacing?: string }>) {
  if (!isVisible) return null;
  return (
    <div className={classNames(buttonFormSpacing, "w-full")}>
      {children ?? <Element is={Button} id="button" buttonType="submit" />}
    </div>
  );
}
